import { __decorate, __metadata } from "tslib";
import { formatSeconds } from '@1inch-community/core/formatters';
import { lazyAppContextConsumer } from '@1inch-community/core/lazy';
import { async, dispatchEvent, getMobileMatchMediaAndSubscribe, observe, translate, } from '@1inch-community/core/lit-utils';
import { getSymbolFromWrapToken, getWrapperNativeToken, isNativeToken, } from '@1inch-community/sdk/chain';
import { SwapContextToken } from '@1inch-community/sdk/swap';
import { buildTokenIdByToken, isTokensEqual } from '@1inch-community/sdk/tokens';
import '@1inch-community/ui-components/button';
import '@1inch-community/ui-components/card';
import '@1inch-community/ui-components/icon';
import '@1inch-community/widgets/notifications';
import { consume } from '@lit/context';
import { html, LitElement } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { choose } from 'lit/directives/choose.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { when } from 'lit/directives/when.js';
import { shareReplay, switchMap } from 'rxjs';
import { UserRejectedRequestError } from 'viem';
import '../../../shared-elements/token-icon';
import { confirmSwapStyle } from './confirm-swap.style';
let ConfirmSwapElement = class ConfirmSwapElement extends LitElement {
    constructor() {
        super(...arguments);
        this.applicationContext = lazyAppContextConsumer(this);
        this.state = null;
        this.swapInProgress = false;
        this.fiatAmountMap = new Map();
        this.mobileMedia = getMobileMatchMediaAndSubscribe(this);
    }
    static { this.tagName = 'inch-confirm-swap'; }
    static { this.styles = confirmSwapStyle; }
    get needWrap() {
        return isNativeToken(this.swapSnapshot.sourceToken.address);
    }
    render() {
        const size = this.mobileMedia.matches ? 'xl' : 'xxl';
        return html `
      <div class="confirm-swap-view">
        <inch-card-header
          backButton
          headerTextPosition="center"
          headerText="Confirm swap"
          headerTextPosition="left"
        >
        </inch-card-header>

        ${this.getTokenViewContainer()} ${this.getDetailInfo()}

        <inch-button
          loader="${ifDefined(this.swapInProgress ? '' : undefined)}"
          @click="${() => this.onSwap()}"
          fullSize
          size="${size}"
          type="${this.swapInProgress ? 'secondary' : 'primary'}"
        >
          ${when(this.swapInProgress, () => html `<span>Confirm swap in wallet</span>`, () => html `<span>Swap</span>`)}
        </inch-button>
      </div>
    `;
    }
    async onSwap() {
        if (this.swapInProgress)
            return;
        try {
            this.swapInProgress = true;
            const hash = await this.swapContext?.swap(this.swapSnapshot);
            dispatchEvent(this, 'backCard', null);
            if (hash) {
                await this.applicationContext.value.notifications.show('Swap status', html `<inch-notification-fusion-swap-view
            orderHash="${hash}"
          ></inch-notification-fusion-swap-view>`, { pinned: true });
            }
        }
        catch (error) {
            const errorText = parseError(error);
            await this.applicationContext.value.notifications.error(html `${translate(errorText)}`);
            console.error(error);
        }
        this.swapInProgress = false;
    }
    getTokenViewContainer() {
        return html `
      <div class="token-view-container">
        ${this.getTokenView(this.swapSnapshot.sourceToken, this.swapSnapshot.sourceTokenAmount, 'source')}
        ${this.getSeparatorView(this.needWrap ? 'wrap' : 'swap')} ${this.getTokenWrapView()}
        ${this.getTokenView(this.swapSnapshot.destinationToken, this.swapSnapshot.destinationTokenAmount, 'destination')}
      </div>
    `;
    }
    getDetailInfo() {
        return html `
      <div class="detail-info">
        ${this.getDetailInfoRow('Price', html `${async(this.getRateView())}`)}
        ${this.getDetailInfoRow('Slippage tolerance', this.getSlippageView())}
        ${this.getDetailInfoRow('Auction time', this.getAuctionTimeView())}
        ${this.getDetailInfoRow('Minimum receive', this.getMinReceive())}
        ${this.getDetailInfoRow('Network Fee', html `
            <div class="detail-info-raw-value detail-info-raw-settings-value">
              <inch-icon icon="fusion16"></inch-icon>
              <span>Free</span>
            </div>
          `)}
      </div>
    `;
    }
    async getRateView() {
        const { rate, revertedRate, sourceToken, destinationToken } = this.swapSnapshot.rate;
        const primaryToken = await this.applicationContext.value.tokenStorage.getPriorityToken(sourceToken.chainId, [sourceToken.address, destinationToken.address]);
        const secondaryToken = isTokensEqual(primaryToken, sourceToken) ? destinationToken : sourceToken;
        const isRevertedRate = isTokensEqual(primaryToken, sourceToken);
        const targetRate = isRevertedRate ? revertedRate : rate;
        const rateFormated = targetRate.toFixedSmart(2);
        return html `
      <div class="detail-info-raw-value">
        <span class="rate-view"
          >1 ${getSymbolFromWrapToken(secondaryToken)} = ${rateFormated}
          ${primaryToken.symbol}</span
        >
      </div>
    `;
    }
    getSlippageView() {
        const slippage = this.swapSnapshot.slippage;
        return html `
      <div class="detail-info-raw-value detail-info-raw-settings-value">
        ${slippage.value === null ? '' : html `<span>${slippage.value}% · </span>`}
        <span>
          ${when(slippage.type === 'auto', () => 'Auto')}
          ${when(slippage.type === 'custom', () => 'Custom')}
          ${when(slippage.type === 'preset', () => 'Manual')}
        </span>
      </div>
    `;
    }
    getAuctionTimeView() {
        const auctionTime = this.swapSnapshot.auctionTime;
        return html `
      <div class="detail-info-raw-value detail-info-raw-settings-value">
        ${auctionTime.value === null
            ? ''
            : html `<span>${formatSeconds(auctionTime.value)} · </span>`}
        <span>
          ${when(auctionTime.type === 'auto', () => 'Auto')}
          ${when(auctionTime.type === 'custom', () => 'Custom')}
          ${when(auctionTime.type === 'preset', () => 'Manual')}
        </span>
      </div>
    `;
    }
    getMinReceive() {
        const amountView = this.swapSnapshot.minReceive.toFixedSmart(6);
        return html `
      <div class="detail-info-raw-value">
        ${amountView} ${this.swapSnapshot.destinationToken.symbol}
      </div>
    `;
    }
    getDetailInfoRow(title, data) {
        return html `
      <div class="detail-info-row">
        <span class="detail-info-row-title">${title}</span>
        <span>${data}</span>
      </div>
    `;
    }
    getTokenView(token, amount, type) {
        const amountView = amount.toFixedSmart(6);
        return html `
      <div class="token-view">
        <div class="token-view-row token-view-top">
          <span>
            ${choose(type, [
            ['source', () => html `You pay`],
            ['wrap', () => html `You wrap`],
            ['destination', () => html `You receive`],
        ])}
          </span>

          <span>${observe(this.getFiatAmountStream(token, amount))}</span>
        </div>
        <div class="token-view-row">
          <div class="symbol-view">
            <inch-token-icon
              chainId="${this.swapSnapshot.sourceToken.chainId}"
              symbol="${token.symbol}"
              address="${token.address}"
            ></inch-token-icon>
            <span class="primary-text">${token.symbol}</span>
          </div>
          <span class="primary-text">${amountView}</span>
        </div>
      </div>
    `;
    }
    getTokenWrapView() {
        if (this.needWrap) {
            return html `
        ${this.getTokenView(getWrapperNativeToken(this.swapSnapshot.sourceToken.chainId), this.swapSnapshot.sourceTokenAmount, 'wrap')}
        ${this.getSeparatorView('swap')}
      `;
        }
        return html ``;
    }
    getSeparatorView(state) {
        return html `
      <div class="separator">
        <div class="separator-view"></div>
        <div
          class="separator-arrow-container ${this.state === state && this.needWrap
            ? 'separator-arrow-container-loader'
            : ''}"
        >
          <inch-icon icon="arrowDown24"></inch-icon>
        </div>
      </div>
    `;
    }
    getFiatAmountStream(token, amount) {
        if (this.fiatAmountMap.has(token.address)) {
            return this.fiatAmountMap.get(token.address);
        }
        const stream = this.applicationContext.value.onChain
            .getBlockEmitter(this.swapSnapshot.sourceToken.chainId)
            .pipe(switchMap(async () => {
            const fiatPrice = await this.applicationContext.value.tokenStorage.getTokenFiatPrice({
                tokenRecordId: buildTokenIdByToken(token),
            });
            const fiatBalance = amount.mul(fiatPrice);
            return `~$${fiatBalance.toFixedSmart(2)}`;
        }), shareReplay({ bufferSize: 1, refCount: true }));
        this.fiatAmountMap.set(token.address, stream);
        return stream;
    }
};
__decorate([
    property({ type: Object }),
    __metadata("design:type", Object)
], ConfirmSwapElement.prototype, "swapSnapshot", void 0);
__decorate([
    consume({ context: SwapContextToken, subscribe: true }),
    property({ type: Object, attribute: false }),
    __metadata("design:type", Object)
], ConfirmSwapElement.prototype, "swapContext", void 0);
__decorate([
    state(),
    __metadata("design:type", Object)
], ConfirmSwapElement.prototype, "state", void 0);
__decorate([
    state(),
    __metadata("design:type", Object)
], ConfirmSwapElement.prototype, "swapInProgress", void 0);
ConfirmSwapElement = __decorate([
    customElement(ConfirmSwapElement.tagName)
], ConfirmSwapElement);
export { ConfirmSwapElement };
function parseError(error) {
    if (error instanceof UserRejectedRequestError) {
        return 'widgets.swap-form.swap-button.wallet-rejected';
    }
    return 'widgets.swap-form.swap-button.swap-error-message';
}
//# sourceMappingURL=confirm-swap.element.js.map